export type WebGLSupport = 'hardware' | 'software' | 'none' | 'unknown';

const IDLE_TIMEOUT_MS = 10_000;

const canDraw = (failIfMajorPerformanceCaveat: boolean) => {
  const context = document
    .createElement('canvas')
    .getContext('webgl2', { failIfMajorPerformanceCaveat });
  // Browsers cap live WebGL contexts per page, so the probe gives its own back.
  context?.getExtension('WEBGL_lose_context')?.loseContext();
  return context !== null;
};

/**
 * Whether this browser draws WebGL 2, which the Alignment Graph needs: on a GPU,
 * only in software (as on virtual desktops without one, where large graphs
 * crawl), or not at all. `unknown` if probing failed.
 */
const detectWebGLSupport = (): WebGLSupport => {
  try {
    if (canDraw(true)) return 'hardware';
    return canDraw(false) ? 'software' : 'none';
  } catch {
    return 'unknown';
  }
};

/** Probes once the page is idle, as creating a context can start the GPU. */
export const probeWebGLSupportWhenIdle = (
  report: (support: WebGLSupport) => void
) => {
  const probe = () => report(detectWebGLSupport());
  if ('requestIdleCallback' in window)
    window.requestIdleCallback(probe, { timeout: IDLE_TIMEOUT_MS });
  else setTimeout(probe, IDLE_TIMEOUT_MS);
};
